import React from 'react';
import { Table, Tag } from 'antd';
import { DOWN_COLOR, FAINT_COLOR, MUTED_COLOR, UP_COLOR } from '../../components/metricColors';
import { kAxis } from '../../components/axisFormat';
import { withKeyboardSort } from '../../components/keyboardSort';

/**
 * 訂房曲線的逐提前天數表格：每格上為今年、下為去年。
 *
 * 與上方曲線是同一份 items，只是換成看得到確切數字的形式 ——
 * 圖看形狀 (超前/落後)，表看「差多少」與「是價格還是量造成的」。
 */

const num = (v) => (v == null ? '—' : Number(v).toLocaleString());
// 走共用的 kAxis：原本的 Math.round(v/1000) 會把 2500 的營收寫成「3k」
const k = (v) => (v == null ? '—' : kAxis(v));
const two = (a, b) => <span>{a}<br /><span style={{ color: MUTED_COLOR }}>{b}</span></span>;
const nowrap = { whiteSpace: 'nowrap' };

function columnsFor(items, curLead) {
  return [
    // 標出「現在」那一列：曲線圖上有一條現在的紅虛線，表格也要對得起來，
    // 否則得自己數哪一列是今年線的端點
    { title: '提前', key: 'lead', width: 116, render: (_, r) => (
      <span style={nowrap}>
        {r.lead === 0 ? '入住日' : `前 ${r.lead} 天`}
        {r.lead === curLead && <Tag color="orange" style={{ marginLeft: 4 }}>現在</Tag>}
      </span>
    ) },
    { title: '在手', key: 'occ', align: 'right', width: 90,
      render: (_, r) => two(r.thisYear != null ? r.thisYear + '%' : '—', r.ly != null ? r.ly + '%' : '—') },
    { title: 'ADR', key: 'adr', align: 'right', width: 90, render: (_, r) => two(num(r.thisYearAdr), num(r.lyAdr)) },
    { title: 'RevPAR', key: 'revpar', align: 'right', width: 100, render: (_, r) => two(num(r.thisYearRevpar), num(r.lyRevpar)) },
    { title: '營收 (較上期)', key: 'rev', align: 'right', width: 150, render: (_, r) => {
        const d = (r.thisYearRev != null && r.lyRev != null) ? r.thisYearRev - r.lyRev : null;
        const up = d == null ? null : d >= 0;
        return (
          <span style={nowrap}>
            <span>{k(r.thisYearRev)}</span>
            <br /><span style={{ color: up == null ? MUTED_COLOR : up ? UP_COLOR : DOWN_COLOR }}>
              {d == null ? '—' : `${up ? '▲ +' : '▼ −'}${k(Math.abs(d))}`}
            </span>
          </span>
        );
      } },
    { title: '較前點 RevPAR%', key: 'delta', align: 'right', width: 130, render: (_, r, idx) => {
        // 標準化為變動率 (%)：RevPAR = ADR × 在手 → %ΔRevPAR ≈ %ΔADR + %Δ量，
        // 直接看 ADR 增減對 RevPAR 的影響
        const prev = items[idx - 1]; // 上一時間點 (較早、提前天數較大)
        const pct = (a, b) => (a != null && b ? (a / b - 1) * 100 : null);
        const dR = pct(r.thisYearRevpar, prev && prev.thisYearRevpar);
        const dA = pct(r.thisYearAdr, prev && prev.thisYearAdr);
        const dO = pct(r.thisYear, prev && prev.thisYear);
        if (!prev || dR == null) return <span style={{ color: FAINT_COLOR }}>—</span>;
        const f = (v) => (v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`);
        return (
          <span style={nowrap}>
            <span style={{ color: dR > 0 ? UP_COLOR : dR < 0 ? DOWN_COLOR : MUTED_COLOR }}>RevPAR {f(dR)}</span>
            <br /><span style={{ color: MUTED_COLOR }}>ADR {f(dA)}·量 {f(dO)}</span>
          </span>
        );
      } },
  ];
}

export default function CurveTable({ curveData, curLead }) {
  const items = (curveData || []).slice().sort((a, b) => b.lead - a.lead);
  return (
    <Table size="small" pagination={false} rowKey="lead" style={{ marginTop: 10 }}
      scroll={{ x: 676 }}
      rowClassName={(r) => (r.lead === curLead ? 'curve-now-row' : '')}
      dataSource={items} columns={withKeyboardSort(columnsFor(items, curLead))} />
  );
}
